import { AccountSearchDto } from '../../../proto/accounts';
import { AppSubjectEnum } from '../../../shared/modules/authz/enums/app-subject.enum';
import { CaslSubject } from '../../../shared/modules/authz/decorators/casl-subject.decorator';
import { BaseDto } from '../../../shared/modules/base/dtos/base.dto';
import { FileDto } from '../../../shared/modules/files/dtos/file.dto';
import { PricingItemDto } from '../../../shared/modules/pricing-items/dtos/pricing-item.dto';
import { ComputedPeriodDurationDto } from '../../../shared/modules/shared/dtos/computed-period-duration.dto';
import { CheckInDto } from '../../check-ins/dtos/check-in.dto';
import { CheckOutDto } from '../../check-outs/dtos/check-out.dto';
import { AgreementContactDto } from '../../contacts/dtos/agreement-contact.dto';
import { CustomerRecordDto, CustomerSearchDto } from '../../customers';
import { LocationSearchDto } from '../../locations';
import { RentalReservationSearchDto } from '../../rental-reservations';
import { PolicyAssignDto } from '../../policies/dtos/policy-assign.dto';
import { TagListDto } from '../../tags/dtos/tag-list.dto';
import { VehicleCategorySearchDto } from '../../vehicle-categories';
import { VehicleSearchDto } from '../../vehicles';
import { RentalAgreementRecordCategoryEnum } from '../enums/rental-agreement-record-category.enum';
import { RentalAgreementStatusEnum } from '../enums/rental-agreement-status.enum';
import { RentalAgreementTypeEnum } from '../enums/rental-agreement-type.enum';
import { RentalPartySourceTypeEnum } from '../enums/rental-party-source-type.enum';

import { RentalAgreementLinksDto } from './rental-agreement-links.dto';

/**
 * The agreement a client reads back. The booker and the drivers are the
 * copies stored on the agreement.
 */
@CaslSubject(AppSubjectEnum.RENTAL_AGREEMENT)
export class RentalAgreementRecordDto extends BaseDto {
  code!: string;
  documentNumber!: string;
  qrCode?: string | null;
  status!: RentalAgreementStatusEnum;
  notes?: string | null;
  recordCategory!: RentalAgreementRecordCategoryEnum;
  hasFullInsurance?: boolean | null;
  agreementContact?: AgreementContactDto | null;
  type!: RentalAgreementTypeEnum;
  startsAt!: Date;
  endsAt!: Date;
  vehicleId?: number | null;
  vehicleCategoryId?: number | null;
  pickupLocationId?: number | null;
  pickupLocation?: LocationSearchDto | null;
  dropOffLocationId?: number | null;
  dropOffLocation?: LocationSearchDto | null;
  rentalReservationId?: number | null;
  rentalReservation?: RentalReservationSearchDto | null;
  /** Written only for a customer booker. An account booker leaves this empty. */
  customerId?: number | null;
  partyType?: RentalPartySourceTypeEnum | null;
  accountUuid?: string | null;
  customer?: CustomerSearchDto | null;
  account?: AccountSearchDto | null;
  agentId?: number | null;
  checkOuts?: CheckOutDto[] | null;
  checkIns?: CheckInDto[] | null;
  vehicle?: VehicleSearchDto | null;
  vehicleCategory?: VehicleCategorySearchDto | null;
  pricingItems?: PricingItemDto[] | null;
  driverCustomers?: CustomerRecordDto[] | null;
  driverAccounts?: AccountSearchDto[] | null;
  tags?: TagListDto[] | null;
  policies?: PolicyAssignDto[] | null;
  links?: RentalAgreementLinksDto | null;
  files?: FileDto[] | null;
  computedPeriod?: ComputedPeriodDurationDto | null;
  computedTotalNet?: number | null;
  computedTotalTax?: number | null;
  computedTotalGross?: number | null;
  computedDiscountNet?: number | null;
  computedDiscountTax?: number | null;
  computedDiscountGross?: number | null;
  computedStandardNet?: number | null;
  computedStandardTax?: number | null;
  computedStandardGross?: number | null;
  computedExtrasNet?: number | null;
  computedExtrasTax?: number | null;
  computedExtrasGross?: number | null;
  /**
   * Broker display-only. Populated by search-cache and quote flows; not used
   * by manage manage pricing apply / hydration.
   */
  computedSavingsAmount?: number | null;
  /**
   * Broker display-only. Populated by search-cache and quote flows; not used
   * by manage manage pricing apply / hydration.
   */
  computedSavingsPercentage?: number | null;
  /**
   * Broker display-only. Populated by search-cache and quote flows; not used
   * by manage manage pricing apply / hydration.
   */
  computedOriginalStandardAmount?: number | null;
  computedCommissionNet?: number | null;
}
